import { pgTable, text, serial, integer, boolean, timestamp, json, jsonb, pgEnum, varchar, real, numeric, unique, uniqueIndex, index, foreignKey } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Enum for entity types (legacy - now using dynamic entity_types table)
// export const entityTypeEnum = pgEnum('entity_type', ['building', 'street', 'square', 'event']);

// Enum for entity status
export const entityStatusEnum = pgEnum('entity_status', ['draft', 'published']);

// Table for dynamic entity types
export const entityTypes = pgTable("entity_types", {
  id: serial("id").primaryKey(),
  code: varchar("code", { length: 50 }).notNull().unique(), // Codice univoco (es. "building")
  name: varchar("name", { length: 100 }).notNull(), // Nome visualizzato (es. "Edificio")
  icon: varchar("icon", { length: 20 }).notNull(), // Emoji o codice icona
  color: varchar("color", { length: 50 }).notNull(), // Nome colore per Chakra UI 
  description: text("description"), // Descrizione opzionale
  // Multilingual fields
  nameEn: varchar("name_en", { length: 100 }), // English name
  nameDe: varchar("name_de", { length: 100 }), // German name
  nameSl: varchar("name_sl", { length: 100 }), // Slovene name
  descriptionEn: text("description_en"), // English description
  descriptionDe: text("description_de"), // German description
  descriptionSl: text("description_sl"), // Slovene description
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow()
});

// Enum for entity relation types
export const entityRelationTypeEnum = pgEnum('entity_relation_type', ['part_of', 'contains', 'near', 'see_also', 'related']);

// Users table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  email: text("email").notNull().unique(), // Email come identificatore primario
  password: text("password"), // Opzionale per social login
  displayName: text("display_name"), // Nome pubblico/alias opzionale
  firstName: text("first_name"), // Per social login
  lastName: text("last_name"), // Per social login
  profileImageUrl: text("profile_image_url"), // Avatar/foto profilo
  isAdmin: boolean("is_admin").default(false),
  isBanned: boolean("is_banned").default(false),
  banReason: text("ban_reason"),
  bannedUntil: timestamp("banned_until"),
  acceptedPrivacyPolicy: boolean("accepted_privacy_policy").notNull().default(false),
  acceptedNewsletter: boolean("accepted_newsletter").notNull().default(false),
  privacyPolicyAcceptedAt: timestamp("privacy_policy_accepted_at"),
  newsletterAcceptedAt: timestamp("newsletter_accepted_at"),
  newsletterPromptSeenAt: timestamp("newsletter_prompt_seen_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Stato dell'iscrizione newsletter. L'iscritto non è un utente: la chiave è
// l'email, così l'ordine fra iscrizione e registrazione è irrilevante.
export const newsletterStatusEnum = pgEnum('newsletter_status', ['pending', 'confirmed', 'unsubscribed']);

export const newsletterSubscribers = pgTable("newsletter_subscribers", {
  id: serial("id").primaryKey(),
  // Normalizzata dal chiamante; il DB la fa rispettare con il CHECK
  // newsletter_subscribers_email_lowercase, altrimenti la UNIQUE
  // case-sensitive lascerebbe convivere Test@x.com e test@x.com.
  email: text("email").notNull().unique(),
  status: newsletterStatusEnum("status").notNull().default('pending'),
  source: text("source").notNull(), // footer | signup | prompt | profile | migration
  language: text("language").notNull().default('it'),
  confirmationTokenHash: text("confirmation_token_hash"),
  confirmationSentAt: timestamp("confirmation_sent_at"),
  confirmedAt: timestamp("confirmed_at"),
  // Nessuna scadenza: il link vive dentro email aperte anche anni dopo.
  unsubscribeToken: text("unsubscribe_token").notNull().unique(),
  unsubscribedAt: timestamp("unsubscribed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  statusIdx: index("newsletter_subscribers_status_idx").on(t.status),
}));

// OAuth providers supported for social login
export const oauthProviderEnum = pgEnum('oauth_provider', ['google', 'facebook']);
export type OAuthProvider = (typeof oauthProviderEnum.enumValues)[number];

// User OAuth accounts table (supports multi-provider + linking)
export const userOauthAccounts = pgTable(
  "user_oauth_accounts",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    provider: oauthProviderEnum("provider").notNull(),
    providerUserId: text("provider_user_id").notNull(),
    email: text("email"),
    emailVerified: boolean("email_verified"),
    profileImageUrl: text("profile_image_url"),
    lastLoginAt: timestamp("last_login_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    providerProviderUserUnique: uniqueIndex("user_oauth_accounts_provider_provider_user_id_uniq").on(
      t.provider,
      t.providerUserId,
    ),
    userIdIdx: index("user_oauth_accounts_user_id_idx").on(t.userId),
  }),
);

// Auth-link verification tokens (OTP) for safe account linking
export const authLinkTokens = pgTable(
  "auth_link_tokens",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    provider: oauthProviderEnum("provider").notNull(),
    providerUserId: text("provider_user_id").notNull(),
    email: text("email").notNull(),
    codeHash: text("code_hash").notNull(),
    attempts: integer("attempts").notNull().default(0),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    lookupIdx: index("auth_link_tokens_lookup_idx").on(t.userId, t.provider, t.providerUserId),
    expiresIdx: index("auth_link_tokens_expires_at_idx").on(t.expiresAt),
  }),
);

// Token per il flusso "password dimenticata": token ad alta entropia (256 bit),
// solo l'hash è salvato — mai il token in chiaro, come per auth_link_tokens.
export const passwordResetTokens = pgTable(
  "password_reset_tokens",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    tokenHash: text("token_hash").notNull().unique(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    userIdIdx: index("password_reset_tokens_user_id_idx").on(t.userId),
  }),
);

// API Keys table for remote access
export const apiKeys = pgTable("api_keys", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  name: text("name").notNull(), // Nome descrittivo per identificare l'uso
  description: text("description"), // Descrizione dettagliata dell'uso
  userId: integer("user_id").notNull().references(() => users.id),
  permissions: text("permissions").array().notNull(), // Permessi specifici (es. ['entities:create', 'entities:update'])
  isActive: boolean("is_active").default(true),
  lastUsed: timestamp("last_used"),
  expiresAt: timestamp("expires_at"), // Scadenza opzionale
  createdAt: timestamp("created_at").defaultNow(),
});

// Default covers table for entity types
export const defaultCovers = pgTable("default_covers", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(), // Nome descrittivo (es. "Copertina Standard Edifici")
  imageUrl: text("image_url").notNull(),
  entityTypeCode: varchar("entity_type_code", { length: 50 })
    .references(() => entityTypes.code, { onDelete: "cascade" }),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0), // Per ordinare le copertine
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id),
});

// Configurazione video YouTube in Homepage (per lingua)
export const homepageVideoSettings = pgTable("homepage_video_settings", {
  language: varchar("language", { length: 2 }).primaryKey(), // it/en/de
  enabled: boolean("enabled").notNull().default(true),
  youtubeVideoId: text("youtube_video_id").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: integer("updated_by").references(() => users.id),
});

// Slider della hero della home: foto selezionate dall'admin, ordinate
// La posizione del soggetto nel frame è per slide (object-position CSS).
export const homepageHeroSlides = pgTable("homepage_hero_slides", {
  photoId: integer("photo_id").primaryKey().references(() => photos.id, { onDelete: "cascade" }),
  position: integer("position").notNull(),
  objectPosition: varchar("object_position", { length: 20 }).notNull().default('50% 50%'),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  createdBy: integer("created_by").references(() => users.id),
}, (t) => ({
  positionIdx: index("homepage_hero_slides_position_idx").on(t.position),
}));

// Modal Conversion Settings
export const modalConversionSettings = pgTable("modal_conversion_settings", {
  id: serial("id").primaryKey(),
  isEnabled: boolean("is_enabled").notNull().default(false),
  triggerSettings: json("trigger_settings").notNull().$type<{
    photoCount: { enabled: boolean; value: number };
    scrollPercentage: { enabled: boolean; value: number };
    timeOnPage: { enabled: boolean; value: number };
  }>(),
  contentSettings: json("content_settings").notNull().$type<{
    it: { title: string; description: string; googleButton: string; facebookButton: string; subtext: string; privacyDisclaimer: string };
    en: { title: string; description: string; googleButton: string; facebookButton: string; subtext: string; privacyDisclaimer: string };
    de: { title: string; description: string; googleButton: string; facebookButton: string; subtext: string; privacyDisclaimer: string };
    sl: { title: string; description: string; googleButton: string; facebookButton: string; subtext: string; privacyDisclaimer: string };
  }>(),
  privacyPolicyUrls: json("privacy_policy_urls").notNull().$type<{
    it: string;
    en: string;
    de: string;
    sl: string;
  }>(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: integer("updated_by").references(() => users.id),
});

// Photos table
export const photos = pgTable("photos", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  imageUrl: text("image_url").notNull(),
  thumbUrl: text("thumb_url"), // URL della thumbnail ottimizzata
  slug: text("slug").unique(), // SEO-friendly URL slug
  year: integer("year"),
  yearEstimated: boolean("year_estimated").default(false),
  lat: text("lat"),
  lng: text("lng"),
  author: text("author"), // <--- AGGIUNTO
  uploadedBy: integer("uploaded_by").references(() => users.id),
  source: text("source"),
  sourceInventory: varchar("source_inventory", { length: 40 }), // Inventario nell'archivio di provenienza (es. F010517)
  sourceUrl: text("source_url"), // URL pubblico della scheda nel catalogo dell'archivio
  license: text("license"),
  status: text("status").default("published"), // Status: 'published', 'draft'
  // Multilingual fields
  titleEn: text("title_en"), // English title
  titleDe: text("title_de"), // German title
  titleSl: text("title_sl"), // Slovene title
  descriptionEn: text("description_en"), // English description
  descriptionDe: text("description_de"), // German description
  descriptionSl: text("description_sl"), // Slovene description
  reverseImageUrl: text("reverse_image_url"), // Retro cartolina (solo full size)
  sourceImageUrl: text("source_image_url"), // Immagine prima del restauro AI (WebP ottimizzato)
  imageWidth: integer("image_width"), // Larghezza immagine originale in pixel (per CLS prevention)
  imageHeight: integer("image_height"), // Altezza immagine originale in pixel (per CLS prevention)
  createdAt: timestamp("created_at").defaultNow(),
  // Bump automatico a ogni update: alimenta dateModified nello structured data (freshness SEO).
  updatedAt: timestamp("updated_at").$onUpdate(() => new Date()),
}, (t) => ({
  yearIdx: index("idx_photos_year").on(t.year),
  statusIdx: index("idx_photos_status").on(t.status),
  statusYearIdx: index("idx_photos_status_year").on(t.status, t.year),
  createdAtIdx: index("idx_photos_created_at").on(t.createdAt),
}));

// Entities table (buildings, streets, squares, events)
export const entities = pgTable("entities", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(), // Nome attuale/moderno
  historicalName: text("historical_name"), // Nome storico/originale
  description: text("description"),
  sources: text("sources"), // Fonti per la descrizione storica
  sourcesApproved: boolean("sources_approved").notNull().default(false), // Approvazione editoriale delle fonti pubbliche
  entityTypeCode: varchar("entity_type_code", { length: 50 })
    .references(() => entityTypes.code, { onDelete: "set null" }),
  slug: text("slug").unique(), // SEO-friendly URL slug
  lat: text("lat"),
  lng: text("lng"),
  coverPhotoId: integer("cover_photo_id").references(() => photos.id),
  defaultCoverId: integer("default_cover_id").references(() => defaultCovers.id),
  coverPhotoPosition: integer("cover_photo_position").default(50), // Posizione dell'immagine (0-100%)
  // URL pubblico dell'audio guida (MP3) su GCS
  audioUrl: text("audio_url"),
  // Indica se l'entità/edificio esiste ancora oggi
  stillExists: boolean("still_exists").default(true),
  // New fields for entity properties
  constructionYear: integer("construction_year"), // Anno di costruzione
  architect: text("architect"), // Architetto
  architecturalStyle: text("architectural_style"), // Stile architettonico
  birthDate: timestamp("birth_date"), // Data di nascita (per persone)
  deathDate: timestamp("death_date"), // Data di morte (per persone)
  birthPlace: text("birth_place"), // Luogo di nascita
  profession: text("profession"), // Professione
  religion: text("religion"), // Religione
  // Multilingual fields
  nameEn: text("name_en"), // English name
  nameDe: text("name_de"), // German name
  nameSl: text("name_sl"), // Slovene name
  historicalNameEn: text("historical_name_en"), // English historical name
  historicalNameDe: text("historical_name_de"), // German historical name
  historicalNameSl: text("historical_name_sl"), // Slovene historical name
  descriptionEn: text("description_en"), // English description
  descriptionDe: text("description_de"), // German description
  descriptionSl: text("description_sl"), // Slovene description
  sourcesEn: text("sources_en"), // English sources
  sourcesDe: text("sources_de"), // German sources
  sourcesSl: text("sources_sl"), // Slovene sources
  audioUrlEn: text("audio_url_en"), // English audio guide URL
  audioUrlDe: text("audio_url_de"), // German audio guide URL
  audioUrlSl: text("audio_url_sl"), // Slovene audio guide URL
  metaDescription: varchar("meta_description", { length: 160 }),
  metaDescriptionEn: varchar("meta_description_en", { length: 160 }),
  metaDescriptionDe: varchar("meta_description_de", { length: 160 }),
  metaDescriptionSl: varchar("meta_description_sl", { length: 160 }),
  // Title SEO per-entità: sovrascrive il template `{name} - Trieste Storica`.
  // 70 di margine; il limite ≤60 consigliato è validato nel content task, non nel DB.
  seoTitle: varchar("seo_title", { length: 70 }),
  seoTitleEn: varchar("seo_title_en", { length: 70 }),
  seoTitleDe: varchar("seo_title_de", { length: 70 }),
  seoTitleSl: varchar("seo_title_sl", { length: 70 }),
  createdAt: timestamp("created_at").defaultNow(),
  // Bump automatico a ogni update: alimenta dateModified nello structured data (freshness SEO).
  updatedAt: timestamp("updated_at").$onUpdate(() => new Date()),
  createdBy: integer("created_by").references(() => users.id),
  // Campi per la ricerca full-text e conteggio foto
  search_vector: text("search_vector"),
  photo_count: integer("photo_count").default(0),
  // Stato dell'entità (pubblicata o bozza)
  status: text("status").default('draft'),
  // Popolarità (calcolata via GA4)
  popularityScore: numeric("popularity_score", { precision: 12, scale: 2 }),
  lastPopularityUpdate: timestamp("last_popularity_update"),
}, (t) => ({
  popularityScoreIdx: index("entities_popularity_score_idx").on(t.popularityScore),
  entityTypeCodeIdx: index("idx_entities_entity_type_code").on(t.entityTypeCode),
  statusIdx: index("idx_entities_status").on(t.status),
  createdAtIdx: index("idx_entities_created_at").on(t.createdAt),
  typeStatusIdx: index("idx_entities_type_status").on(t.entityTypeCode, t.status),
}));

// Tags table
export const tags = pgTable("tags", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  nameEn: text("name_en"), // English name
  nameDe: text("name_de"), // German name
  nameSl: text("name_sl"), // Slovene name
  createdAt: timestamp("created_at").defaultNow(),
});

// Photo-tag relationship table
export const photoTags = pgTable("photo_tags", {
  id: serial("id").primaryKey(),
  photoId: integer("photo_id").references(() => photos.id).notNull(),
  tagId: integer("tag_id").references(() => tags.id).notNull(),
});

// Photo-entity relationship table
export const photoEntities = pgTable("photo_entities", {
  id: serial("id").primaryKey(),
  photoId: integer("photo_id").references(() => photos.id).notNull(),
  entityId: integer("entity_id").references(() => entities.id).notNull(),
}, (t) => ({
  photoIdIdx: index("idx_photo_entities_photo_id").on(t.photoId),
  entityIdIdx: index("idx_photo_entities_entity_id").on(t.entityId),
  photoEntityIdx: index("idx_photo_entities_photo_entity").on(t.photoId, t.entityId),
}));

// Entity-entity relationship table for correlating historical sheets
export const entityRelations = pgTable("entity_relations", {
  id: serial("id").primaryKey(),
  entityId: integer("entity_id").references(() => entities.id).notNull(),
  relatedEntityId: integer("related_entity_id").references(() => entities.id).notNull(),
  relationType: entityRelationTypeEnum("relation_type").default('related').notNull(),
  description: text("description"), // Optional description of the relationship
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id),
}, (t) => ({
  pairUnique: unique("entity_relations_entity_id_related_entity_id_unique").on(t.entityId, t.relatedEntityId),
}));

// User favorites
export const userFavorites = pgTable("user_favorites", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  photoId: integer("photo_id").references(() => photos.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// User bookmarks for historical entities
export const userBookmarks = pgTable("user_bookmarks", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  entityId: integer("entity_id").references(() => entities.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  pairUnique: unique("user_bookmarks_user_id_entity_id_unique").on(t.userId, t.entityId),
}));

// User recently viewed photos
export const userRecentPhotos = pgTable("user_recent_photos", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  photoId: integer("photo_id").references(() => photos.id, { onDelete: "cascade" }).notNull(),
  viewedAt: timestamp("viewed_at").defaultNow().notNull(),
});

// Photo annotations table
export const annotations = pgTable("annotations", {
  id: serial("id").primaryKey(),
  photoId: integer("photo_id").references(() => photos.id).notNull(),
  entityId: integer("entity_id").references(() => entities.id).notNull(),
  xPercent: real("x_percent").notNull(), // Posizione X in percentuale (0-100)
  yPercent: real("y_percent").notNull(), // Posizione Y in percentuale (0-100)
  widthPercent: real("width_percent").notNull(), // Larghezza in percentuale (0-100)
  heightPercent: real("height_percent").notNull(), // Altezza in percentuale (0-100)
  tooltipText: text("tooltip_text").notNull(), // Testo del tooltip
  description: text("description"), // Descrizione opzionale dell'annotazione
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Create insert schemas using drizzle-zod
export const insertUserSchema = createInsertSchema(users)
  .omit({ id: true, createdAt: true, isAdmin: true, isBanned: true, banReason: true, bannedUntil: true });

export const insertNewsletterSubscriberSchema = createInsertSchema(newsletterSubscribers)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertUserOauthAccountSchema = createInsertSchema(userOauthAccounts)
  .omit({ id: true, createdAt: true, updatedAt: true, lastLoginAt: true });

export const insertAuthLinkTokenSchema = createInsertSchema(authLinkTokens)
  .omit({ id: true, createdAt: true, attempts: true });

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens)
  .omit({ id: true, createdAt: true });

export const insertPhotoSchema = createInsertSchema(photos)
  .omit({ id: true, createdAt: true });

export const insertEntitySchema = createInsertSchema(entities)
  .omit({ id: true, createdAt: true, search_vector: true, photo_count: true });

export const insertTagSchema = createInsertSchema(tags)
  .omit({ id: true, createdAt: true });

export const insertPhotoTagSchema = createInsertSchema(photoTags)
  .omit({ id: true });

export const insertAnnotationSchema = createInsertSchema(annotations)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    xPercent: z.number(),
    yPercent: z.number(),
    widthPercent: z.number(),
    heightPercent: z.number()
  });

export const insertPhotoEntitySchema = createInsertSchema(photoEntities)
  .omit({ id: true });

export const insertEntityRelationSchema = createInsertSchema(entityRelations)
  .omit({ id: true, createdAt: true });

export const insertUserFavoriteSchema = createInsertSchema(userFavorites)
  .omit({ id: true, createdAt: true });

export const insertUserBookmarkSchema = createInsertSchema(userBookmarks)
  .omit({ id: true, createdAt: true });

export const insertUserRecentPhotoSchema = createInsertSchema(userRecentPhotos)
  .omit({ id: true });

export const insertEntityTypeSchema = createInsertSchema(entityTypes)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertModalConversionSettingsSchema = createInsertSchema(modalConversionSettings)
  .omit({ id: true, updatedAt: true, updatedBy: true })
  .transform((data) => {
    // Remove old privacyPolicyUrl field if present
    const { privacyPolicyUrl, ...rest } = data as any;
    return rest;
  })
  .refine((data) => {
    // Ensure privacyPolicyUrls exists and has all required languages
    if (!data.privacyPolicyUrls) return false;
    const urls = data.privacyPolicyUrls as any;
    return urls.it && urls.en && urls.de && urls.sl &&
           typeof urls.it === 'string' && typeof urls.en === 'string' &&
           typeof urls.de === 'string' && typeof urls.sl === 'string';
  }, {
    message: "privacyPolicyUrls must contain it, en, de, and sl as strings"
  });

// Define types
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type NewsletterSubscriber = typeof newsletterSubscribers.$inferSelect;
export type InsertNewsletterSubscriber = z.infer<typeof insertNewsletterSubscriberSchema>;

export type UserOauthAccount = typeof userOauthAccounts.$inferSelect;
export type InsertUserOauthAccount = z.infer<typeof insertUserOauthAccountSchema>;

export type AuthLinkToken = typeof authLinkTokens.$inferSelect;
export type InsertAuthLinkToken = z.infer<typeof insertAuthLinkTokenSchema>;

export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;

export type Photo = typeof photos.$inferSelect;
export type InsertPhoto = z.infer<typeof insertPhotoSchema>;

export type Entity = typeof entities.$inferSelect;
export type InsertEntity = z.infer<typeof insertEntitySchema>;

export type Tag = typeof tags.$inferSelect;
export type InsertTag = z.infer<typeof insertTagSchema>;

export type PhotoTag = typeof photoTags.$inferSelect;
export type InsertPhotoTag = z.infer<typeof insertPhotoTagSchema>;

export type PhotoEntity = typeof photoEntities.$inferSelect;
export type InsertPhotoEntity = z.infer<typeof insertPhotoEntitySchema>;

export type EntityRelation = typeof entityRelations.$inferSelect;
export type InsertEntityRelation = z.infer<typeof insertEntityRelationSchema>;

export type UserFavorite = typeof userFavorites.$inferSelect;
export type InsertUserFavorite = z.infer<typeof insertUserFavoriteSchema>;

export type UserBookmark = typeof userBookmarks.$inferSelect;
export type InsertUserBookmark = z.infer<typeof insertUserBookmarkSchema>;

export type UserRecentPhoto = typeof userRecentPhotos.$inferSelect;
export type InsertUserRecentPhoto = z.infer<typeof insertUserRecentPhotoSchema>;

export type EntityType = typeof entityTypes.$inferSelect;
export type InsertEntityType = z.infer<typeof insertEntityTypeSchema>;

export type ModalConversionSettings = typeof modalConversionSettings.$inferSelect;
export type InsertModalConversionSettings = z.infer<typeof insertModalConversionSettingsSchema>;

// Define photo with related entities for frontend consumption
export type PhotoWithDetails = Photo & {
  tags?: { id: number; name: string }[];
  entities?: { id: number; name: string; entityType: string; entityTypeCode?: string | null; slug?: string | null; coverPhoto?: any }[];
  contributor?: { id: number; displayName: string };
  isFavorite?: boolean;
  dateAdded?: string;
};

// Photo versions table per tenere traccia delle modifiche
export const photoVersions = pgTable("photo_versions", {
  id: serial("id").primaryKey(),
  photoId: integer("photo_id").references(() => photos.id).notNull(),
  versionNumber: integer("version_number").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  year: integer("year"),
  yearEstimated: boolean("year_estimated").default(false),
  lat: text("lat"),
  lng: text("lng"),
  source: text("source"),
  author: text("author"), // <--- AGGIUNTO
  license: text("license"),
  approvedBy: integer("approved_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPhotoVersionSchema = createInsertSchema(photoVersions)
  .omit({ id: true, createdAt: true });

export type PhotoVersion = typeof photoVersions.$inferSelect;
export type InsertPhotoVersion = z.infer<typeof insertPhotoVersionSchema>;

// Define entity with related photos for frontend consumption
export type EntityWithDetails = Entity & {
  coverPhoto?: Photo;
  photos?: Photo[];
  photoCount?: number;
  coverPhotoPosition?: number | null;
};

// API Keys schemas and types
export const insertApiKeySchema = createInsertSchema(apiKeys)
  .omit({ id: true, lastUsed: true, createdAt: true });

export const insertDefaultCoverSchema = createInsertSchema(defaultCovers)
  .omit({ id: true, createdAt: true });

export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;
export type DefaultCover = typeof defaultCovers.$inferSelect;
export type InsertDefaultCover = z.infer<typeof insertDefaultCoverSchema>;

export const insertHomepageVideoSettingSchema = createInsertSchema(homepageVideoSettings)
  .omit({ updatedAt: true });

export type HomepageVideoSetting = typeof homepageVideoSettings.$inferSelect;
export type InsertHomepageVideoSetting = z.infer<typeof insertHomepageVideoSettingSchema>;

export const insertHomepageHeroSlideSchema = createInsertSchema(homepageHeroSlides)
  .omit({ createdAt: true });

export type HomepageHeroSlide = typeof homepageHeroSlides.$inferSelect;
export type InsertHomepageHeroSlide = z.infer<typeof insertHomepageHeroSlideSchema>;

// Buildings table for "Mappa Anni di Costruzione Edifici"
export const buildings = pgTable("buildings", {
  id: serial("id").primaryKey(),
  name: text("name"), // Non tutti i palazzi hanno un nome
  constructionYear: integer("construction_year").notNull(), // Anno di costruzione
  architect: text("architect"), // Nome dell'architetto
  architecturalStyle: text("architectural_style"), // Es. Neoclassico, Liberty, Eclettico
  buildingType: text("building_type"), // Es. palazzo, chiesa, teatro, edificio pubblico
  lat: text("lat").notNull(), // Latitudine
  lng: text("lng").notNull(), // Longitudine
  address: text("address"), // Via/piazza per riferimento
  entityId: integer("entity_id").references(() => entities.id), // Link alla scheda storica (entities.id)
  notes: text("notes"), // Note libere
  createdAt: timestamp("created_at").defaultNow(), // Data creazione
  updatedAt: timestamp("updated_at").defaultNow(), // Data ultima modifica
  createdBy: integer("created_by").references(() => users.id).notNull(), // Utente che ha inserito (users.id)
});

export const insertBuildingSchema = createInsertSchema(buildings)
  .omit({ id: true, createdAt: true, updatedAt: true, createdBy: true });

export type Building = typeof buildings.$inferSelect;
export type InsertBuilding = z.infer<typeof insertBuildingSchema>;

// =============================================
// ITINERARI STORICI
// =============================================

export const itineraries = pgTable("itineraries", {
  id: serial("id").primaryKey(),

  // Contenuti multilingue (IT = campo base, EN/DE/SL optional)
  title: text("title").notNull(),
  titleEn: text("title_en"),
  titleDe: text("title_de"),
  titleSl: text("title_sl"),

  description: text("description").notNull(),
  descriptionEn: text("description_en"),
  descriptionDe: text("description_de"),
  descriptionSl: text("description_sl"),

  // SEO
  slug: text("slug").notNull().unique(),
  metaDescription: varchar("meta_description", { length: 160 }),
  metaDescriptionEn: varchar("meta_description_en", { length: 160 }),
  metaDescriptionDe: varchar("meta_description_de", { length: 160 }),
  metaDescriptionSl: varchar("meta_description_sl", { length: 160 }),

  // Media & Metadata
  coverImageUrl: text("cover_image_url"),
  tags: text("tags").array(),
  estimatedDurationMinutes: integer("estimated_duration_minutes"),

  // Walking route (GeoJSON LineString pre-calcolato via OSRM)
  walkingRouteGeoJson: jsonb("walking_route_geojson"),

  // Status & Tracking
  status: text("status").default("draft").notNull(),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id),

  // Approfondimento usato come intro dell'itinerario (opzionale)
  introEssayId: integer("intro_essay_id").references((): any => essays.id, { onDelete: "set null" }),
});

export const itineraryStops = pgTable("itinerary_stops", {
  id: serial("id").primaryKey(),

  // Relazioni
  itineraryId: integer("itinerary_id")
    .notNull()
    .references(() => itineraries.id, { onDelete: "cascade" }),
  entityId: integer("entity_id")
    .notNull()
    .references(() => entities.id, { onDelete: "restrict" }),

  // Ordinamento
  sortOrder: integer("sort_order").notNull(),

  // Metadati specifici per questa tappa (4 lingue)
  curatorNotes: text("curator_notes"),
  curatorNotesEn: text("curator_notes_en"),
  curatorNotesDe: text("curator_notes_de"),
  curatorNotesSl: text("curator_notes_sl"),

  highlight: varchar("highlight", { length: 200 }),
  highlightEn: varchar("highlight_en", { length: 200 }),
  highlightDe: varchar("highlight_de", { length: 200 }),
  highlightSl: varchar("highlight_sl", { length: 200 }),

  suggestedDurationMinutes: integer("suggested_duration_minutes"),

  createdAt: timestamp("created_at").defaultNow(),
});

export const insertItinerarySchema = createInsertSchema(itineraries)
  .omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true });
export const insertItineraryStopSchema = createInsertSchema(itineraryStops)
  .omit({ id: true, createdAt: true });

export type Itinerary = typeof itineraries.$inferSelect;
export type InsertItinerary = z.infer<typeof insertItinerarySchema>;
export type ItineraryStop = typeof itineraryStops.$inferSelect;
export type InsertItineraryStop = z.infer<typeof insertItineraryStopSchema>;

// Rioni storici: le entità neighborhood restano la fonte della storia lunga.
export const neighborhoodPages = pgTable("neighborhood_pages", {
  id: serial("id").primaryKey(),
  entityId: integer("entity_id").notNull().references(() => entities.id, { onDelete: "cascade" }).unique(),
  status: text("status").notNull().default("draft"),
  leadIt: text("lead_it"),
  leadEn: text("lead_en"),
  leadDe: text("lead_de"),
  leadSl: text("lead_sl"),
  heroPhotoId: integer("hero_photo_id").references(() => photos.id, { onDelete: "set null" }),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  publishedAt: timestamp("published_at"),
}, (t) => ({
  statusOrderIdx: index("neighborhood_pages_status_order_idx").on(t.status, t.sortOrder),
}));

export const neighborhoodSections = pgTable("neighborhood_sections", {
  id: serial("id").primaryKey(),
  pageId: integer("page_id").notNull().references(() => neighborhoodPages.id, { onDelete: "cascade" }),
  titleIt: text("title_it"),
  titleEn: text("title_en"),
  titleDe: text("title_de"),
  titleSl: text("title_sl"),
  introIt: text("intro_it"),
  introEn: text("intro_en"),
  introDe: text("intro_de"),
  introSl: text("intro_sl"),
  sortOrder: integer("sort_order").notNull(),
}, (t) => ({
  pageOrderUnique: unique("neighborhood_sections_page_order_unique").on(t.pageId, t.sortOrder),
  pageIdIdUnique: unique("neighborhood_sections_page_id_id_unique").on(t.pageId, t.id),
}));

export const neighborhoodMemberships = pgTable("neighborhood_memberships", {
  id: serial("id").primaryKey(),
  pageId: integer("page_id").notNull().references(() => neighborhoodPages.id, { onDelete: "cascade" }),
  entityId: integer("entity_id").notNull().references(() => entities.id, { onDelete: "restrict" }),
  sectionId: integer("section_id"),
  role: text("role").notNull().default("place"),
  sortOrder: integer("sort_order").notNull().default(0),
}, (t) => ({
  pageEntityUnique: unique("neighborhood_memberships_page_entity_unique").on(t.pageId, t.entityId),
  pageSectionFk: foreignKey({ columns: [t.pageId, t.sectionId], foreignColumns: [neighborhoodSections.pageId, neighborhoodSections.id], name: "neighborhood_memberships_page_section_fkey" }).onDelete("cascade"),
  entityIdx: index("neighborhood_memberships_entity_idx").on(t.entityId),
  sectionOrderIdx: index("neighborhood_memberships_section_order_idx").on(t.sectionId, t.sortOrder),
}));

export type NeighborhoodPage = typeof neighborhoodPages.$inferSelect;
export type NeighborhoodSection = typeof neighborhoodSections.$inferSelect;
export type NeighborhoodMembership = typeof neighborhoodMemberships.$inferSelect;

// =============================================
// APPROFONDIMENTI STORICI (essays)
// =============================================

export const essayCategories = pgTable("essay_categories", {
  id: serial("id").primaryKey(),
  slug: varchar("slug", { length: 80 }).notNull().unique(),
  // null = categoria di primo livello, valorizzato = sottocategoria
  parentId: integer("parent_id").references((): any => essayCategories.id, { onDelete: "restrict" }),

  // Multilingua (IT base, EN/DE/SL opzionali)
  name: varchar("name", { length: 120 }).notNull(),
  nameEn: varchar("name_en", { length: 120 }),
  nameDe: varchar("name_de", { length: 120 }),
  nameSl: varchar("name_sl", { length: 120 }),

  description: text("description"),
  descriptionEn: text("description_en"),
  descriptionDe: text("description_de"),
  descriptionSl: text("description_sl"),

  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const essays = pgTable("essays", {
  id: serial("id").primaryKey(),

  // Identificativi
  slug: varchar("slug", { length: 120 }).notNull().unique(),
  status: text("status").default("draft").notNull(), // draft | published | archived
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id),

  // Tassonomia (foglia: categoria o sottocategoria)
  categoryId: integer("category_id").references(() => essayCategories.id, { onDelete: "restrict" }),

  // Hero
  heroPhotoId: integer("hero_photo_id").references(() => photos.id, { onDelete: "set null" }),
  heroImageUrl: text("hero_image_url"),

  // CTA itinerario consigliato (opzionale)
  suggestedItineraryId: integer("suggested_itinerary_id").references(() => itineraries.id, { onDelete: "set null" }),

  // Autore (default 'Trieste Storica')
  author: varchar("author", { length: 120 }).default("Admin"),

  // Multilingua: titolo
  title: varchar("title", { length: 200 }).notNull(),
  titleEn: varchar("title_en", { length: 200 }),
  titleDe: varchar("title_de", { length: 200 }),
  titleSl: varchar("title_sl", { length: 200 }),

  // Multilingua: excerpt (~280 char, usato come meta description e nelle card)
  excerpt: varchar("excerpt", { length: 280 }).notNull(),
  excerptEn: varchar("excerpt_en", { length: 280 }),
  excerptDe: varchar("excerpt_de", { length: 280 }),
  excerptSl: varchar("excerpt_sl", { length: 280 }),

  // Multilingua: body (markdown + block syntax :::photo / :::gallery)
  body: text("body").notNull(),
  bodyEn: text("body_en"),
  bodyDe: text("body_de"),
  bodySl: text("body_sl"),

  // Override SEO (opzionale, fallback a excerpt)
  metaDescription: varchar("meta_description", { length: 160 }),
  metaDescriptionEn: varchar("meta_description_en", { length: 160 }),
  metaDescriptionDe: varchar("meta_description_de", { length: 160 }),
  metaDescriptionSl: varchar("meta_description_sl", { length: 160 }),

  // Fonti, come nelle schede: IT base + traduzioni senza fallback, pubblicate solo se approvate
  sources: text("sources"),
  sourcesEn: text("sources_en"),
  sourcesDe: text("sources_de"),
  sourcesSl: text("sources_sl"),
  sourcesApproved: boolean("sources_approved").notNull().default(false),

  // Calcolato auto a save (Math.ceil(wordCount / 200))
  readingMinutes: integer("reading_minutes"),

  // URL CDN del podcast NotebookLM (solo IT per ora), es. https://media.triestestorica.it/podcast/{slug}.mp3
  podcastUrl: text("podcast_url"),
});

// Schede correlate manuali ("Vedi anche")
export const essayEntities = pgTable("essay_entities", {
  id: serial("id").primaryKey(),
  essayId: integer("essay_id")
    .notNull()
    .references(() => essays.id, { onDelete: "cascade" }),
  entityId: integer("entity_id")
    .notNull()
    .references(() => entities.id, { onDelete: "cascade" }),
  sortOrder: integer("sort_order").default(0).notNull(),
}, (t) => ({
  essayIdx: index("idx_essay_entities_essay_id").on(t.essayId),
  entityIdx: index("idx_essay_entities_entity_id").on(t.entityId),
}));

// Cache reciproca: per la sezione "Approfondimenti correlati" in pagina scheda.
// Popolata a save di un essay parsando body (link inline) + relazioni manuali.
export const entityEssaysCache = pgTable("entity_essays_cache", {
  id: serial("id").primaryKey(),
  entityId: integer("entity_id")
    .notNull()
    .references(() => entities.id, { onDelete: "cascade" }),
  essayId: integer("essay_id")
    .notNull()
    .references(() => essays.id, { onDelete: "cascade" }),
  source: varchar("source", { length: 20 }).notNull(), // 'manual' | 'inline-link'
}, (t) => ({
  entityIdx: index("idx_entity_essays_cache_entity_id").on(t.entityId),
  essayIdx: index("idx_entity_essays_cache_essay_id").on(t.essayId),
}));

export const insertEssayCategorySchema = createInsertSchema(essayCategories)
  .omit({ id: true, createdAt: true });
export const insertEssaySchema = createInsertSchema(essays)
  .omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true });
export const insertEssayEntitySchema = createInsertSchema(essayEntities);
export const insertEntityEssayCacheSchema = createInsertSchema(entityEssaysCache);

export type EssayCategory = typeof essayCategories.$inferSelect;
export type InsertEssayCategory = z.infer<typeof insertEssayCategorySchema>;
export type Essay = typeof essays.$inferSelect;
export type InsertEssay = z.infer<typeof insertEssaySchema>;
export type EssayEntity = typeof essayEntities.$inferSelect;
export type InsertEssayEntity = z.infer<typeof insertEssayEntitySchema>;
export type EntityEssayCache = typeof entityEssaysCache.$inferSelect;
export type InsertEntityEssayCache = z.infer<typeof insertEntityEssayCacheSchema>;

// Categoria con figli (per albero gerarchico nelle API e UI)
export type EssayCategoryWithChildren = EssayCategory & {
  children: EssayCategory[];
};

// Essay con relazioni risolte (per pagina dettaglio)
export type EssayWithDetails = Essay & {
  category: EssayCategory | null;
  parentCategory: EssayCategory | null;
  heroPhoto: Photo | null;
  suggestedItinerary: Itinerary | null;
  relatedEntities: Entity[];
};

// ============================================================
// QUIZ STORICI
// ============================================================

// Livelli del quiz. Nomi e badge sono editabili da admin senza deploy:
// non replicare mai queste stringhe nel codice o nei file i18n.
// Lo slug invece è immutabile: finisce negli URL condivisi.
export const quizLevels = pgTable("quiz_levels", {
  id: serial("id").primaryKey(),
  position: integer("position").notNull().unique(),
  slug: varchar("slug", { length: 60 }).notNull().unique(),

  name: varchar("name", { length: 120 }).notNull(),
  nameEn: varchar("name_en", { length: 120 }),
  nameDe: varchar("name_de", { length: 120 }),
  nameSl: varchar("name_sl", { length: 120 }),

  description: text("description"),
  descriptionEn: text("description_en"),
  descriptionDe: text("description_de"),
  descriptionSl: text("description_sl"),

  badgeLabel: varchar("badge_label", { length: 120 }).notNull(),
  badgeLabelEn: varchar("badge_label_en", { length: 120 }),
  badgeLabelDe: varchar("badge_label_de", { length: 120 }),
  badgeLabelSl: varchar("badge_label_sl", { length: 120 }),
  badgeImageUrl: text("badge_image_url"),

  passThreshold: integer("pass_threshold").notNull().default(80),
  questionsPerAttempt: integer("questions_per_attempt").notNull().default(10),
  isPublished: boolean("is_published").notNull().default(false),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Pool domande. status: draft | approved | rejected | archived
// Le domande non si cancellano mai: si archiviano (i tentativi storici le referenziano).
export const quizQuestionTypeEnum = pgEnum("quiz_question_type", ["multiple_choice", "photo"]);
export const quizQuestionStatusEnum = pgEnum("quiz_question_status", ["draft", "approved", "rejected", "archived"]);
export const quizGeneratedByEnum = pgEnum("quiz_generated_by", ["ai", "manual"]);

export const quizQuestions = pgTable("quiz_questions", {
  id: serial("id").primaryKey(),
  levelId: integer("level_id").notNull().references(() => quizLevels.id),
  type: quizQuestionTypeEnum("type").notNull().default("multiple_choice"),
  status: quizQuestionStatusEnum("status").notNull().default("draft"),

  text: text("text").notNull(),
  textEn: text("text_en"),
  textDe: text("text_de"),
  textSl: text("text_sl"),

  explanation: text("explanation").notNull(),
  explanationEn: text("explanation_en"),
  explanationDe: text("explanation_de"),
  explanationSl: text("explanation_sl"),

  photoId: integer("photo_id").references(() => photos.id, { onDelete: "set null" }),
  sourceEntityId: integer("source_entity_id").references(() => entities.id, { onDelete: "set null" }),
  sourceEssayId: integer("source_essay_id").references(() => essays.id, { onDelete: "set null" }),
  sourceItineraryId: integer("source_itinerary_id").references(() => itineraries.id, { onDelete: "set null" }),
  // Frase originale da cui nasce la domanda. Serve alla revisione admin, mai mostrata all'utente.
  sourceExcerpt: text("source_excerpt"),

  generatedBy: quizGeneratedByEnum("generated_by").notNull().default("ai"),
  reviewedBy: integer("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),

  timesServed: integer("times_served").notNull().default(0),
  timesCorrect: integer("times_correct").notNull().default(0),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  levelStatusIdx: index("quiz_questions_level_status_idx").on(table.levelId, table.status),
}));

// Opzioni di risposta. Invariante applicativa: 4 righe per domanda, 1 sola con isCorrect.
export const quizAnswers = pgTable("quiz_answers", {
  id: serial("id").primaryKey(),
  questionId: integer("question_id").notNull().references(() => quizQuestions.id, { onDelete: "cascade" }),

  text: text("text").notNull(),
  textEn: text("text_en"),
  textDe: text("text_de"),
  textSl: text("text_sl"),

  isCorrect: boolean("is_correct").notNull().default(false),
  position: integer("position").notNull().default(0),
}, (table) => ({
  questionIdx: index("quiz_answers_question_idx").on(table.questionId),
  questionPositionUnique: uniqueIndex("quiz_answers_question_position_unique")
    .on(table.questionId, table.position),
}));

// Tentativo. userId nullo = tentativo anonimo su L1, agganciato dopo la registrazione.
export const quizAttempts = pgTable("quiz_attempts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
  anonSessionId: varchar("anon_session_id", { length: 64 }),
  levelId: integer("level_id").notNull().references(() => quizLevels.id),

  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  score: integer("score"),
  passed: boolean("passed"),

  // Set congelato all'avvio: le domande si ricaricano per id, senza filtro su status.
  questionIds: jsonb("question_ids").notNull(),
}, (table) => ({
  userIdx: index("quiz_attempts_user_idx").on(table.userId),
  anonIdx: index("quiz_attempts_anon_idx").on(table.anonSessionId),
}));

// Una riga per risposta data. Il vincolo unique rende atomico e idempotente
// il primo invio anche con due richieste concorrenti.
export const quizAttemptAnswers = pgTable("quiz_attempt_answers", {
  id: serial("id").primaryKey(),
  attemptId: integer("attempt_id").notNull().references(() => quizAttempts.id, { onDelete: "cascade" }),
  questionId: integer("question_id").notNull().references(() => quizQuestions.id),
  answerId: integer("answer_id").notNull().references(() => quizAnswers.id),
  // Snapshot dell'esito al momento della risposta. Le domande pubblicate sono
  // immutabili, ma questo evita comunque di ricalcolare la storia dal pool corrente.
  isCorrect: boolean("is_correct").notNull(),
  answeredAt: timestamp("answered_at").notNull().defaultNow(),
}, (table) => ({
  attemptQuestionUnique: uniqueIndex("quiz_attempt_answers_attempt_question_unique")
    .on(table.attemptId, table.questionId),
  attemptIdx: index("quiz_attempt_answers_attempt_idx").on(table.attemptId),
}));

// Possesso badge. Un badge per livello per utente.
export const userBadges = pgTable("user_badges", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  levelId: integer("level_id").notNull().references(() => quizLevels.id),
  attemptId: integer("attempt_id").references(() => quizAttempts.id, { onDelete: "set null" }),
  earnedAt: timestamp("earned_at").defaultNow(),

  shareToken: varchar("share_token", { length: 64 }).unique(),
  shareImageUrl: text("share_image_url"),
  shareNameSnapshot: text("share_name_snapshot"),
  sharedAt: timestamp("shared_at"),
}, (table) => ({
  userLevelUnique: uniqueIndex("user_badges_user_level_unique").on(table.userId, table.levelId),
}));

export type QuizLevel = typeof quizLevels.$inferSelect;
export type InsertQuizLevel = typeof quizLevels.$inferInsert;
export type QuizQuestion = typeof quizQuestions.$inferSelect;
export type InsertQuizQuestion = typeof quizQuestions.$inferInsert;
export type QuizAnswer = typeof quizAnswers.$inferSelect;
export type InsertQuizAnswer = typeof quizAnswers.$inferInsert;
export type QuizAttempt = typeof quizAttempts.$inferSelect;
export type InsertQuizAttempt = typeof quizAttempts.$inferInsert;
export type QuizAttemptAnswer = typeof quizAttemptAnswers.$inferSelect;
export type InsertQuizAttemptAnswer = typeof quizAttemptAnswers.$inferInsert;
export type UserBadge = typeof userBadges.$inferSelect;
export type InsertUserBadge = typeof userBadges.$inferInsert;
